import type { WalletPassDesignDocument, WalletPassKind } from "@lemmonhq/types";
import membership from "./defaults/membership.json";
import coin from "./defaults/coin.json";
import reservation from "./defaults/reservation.json";
import giftCard from "./defaults/gift_card.json";
import { parseWalletPassDesign } from "./schema";

/**
 * The plain default design per kind: neutral colours, no assets, a few placed
 * fields and a QR code. Used when the editor starts a new design and when a
 * program is created without choosing one (the tRPC layer passes this to the
 * program create function, which inserts it as a real design row).
 *
 * The JSON files are the only copy of the defaults. They are parsed with the
 * shared schema here, so an invalid default fails at startup and in tests.
 */
const SOURCES: Record<WalletPassKind, unknown> = {
  membership,
  coin,
  reservation,
  gift_card: giftCard,
};

/**
 * Vite/esbuild inline a JSON import as an object, but `apps/web`'s wrangler
 * config has a `Data` rule for `**\/*.json`, so the worker bundle hands us an
 * ArrayBuffer (sometimes behind `default`) instead. Decode it back to JSON.
 */
const resolveJsonModule = (loaded: unknown): unknown => {
  let candidate = loaded;
  while (
    candidate &&
    typeof candidate === "object" &&
    !(candidate instanceof ArrayBuffer) &&
    !ArrayBuffer.isView(candidate) &&
    "default" in candidate
  ) {
    candidate = (candidate as { default: unknown }).default;
  }
  if (candidate instanceof ArrayBuffer)
    return JSON.parse(new TextDecoder().decode(new Uint8Array(candidate)));
  if (ArrayBuffer.isView(candidate))
    return JSON.parse(
      new TextDecoder().decode(
        new Uint8Array(
          candidate.buffer,
          candidate.byteOffset,
          candidate.byteLength,
        ),
      ),
    );
  return candidate;
};

const parsed = new Map<WalletPassKind, WalletPassDesignDocument>();

export const createDefaultWalletPassDesign = (
  kind: WalletPassKind,
): WalletPassDesignDocument => {
  let document = parsed.get(kind);
  if (!document) {
    document = parseWalletPassDesign(
      kind,
      resolveJsonModule(SOURCES[kind]),
    );
    parsed.set(kind, document);
  }
  return structuredClone(document);
};
